import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  VStack,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Avatar,
  IconButton,
  useColorModeValue,
  Spinner,
  Badge,
  Alert,
  AlertIcon,
  Button,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Send, Mic, MessageCircle, Clock, AlertCircle, Heart, Brain, Zap, BookOpen } from 'lucide-react';
import { sendMessageToAnthropic } from '../services/anthropic';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: string;
  sources?: string[];
}

interface SuggestionTopic {
  id: string;
  text: string;
  icon: React.FC;
}

const methodistColors = {
  blue: '#0066CC',
  navy: '#002D72',
  green: '#38A169',
  purple: '#805AD5',
};

// Maximum number of questions allowed
const MAX_QUESTIONS = 10;

const MobileResilienceAssistant: React.FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: 'Welcome to the SentientIQ Wellness Companion! I\'m JIMI, your personalized wellness coach. I can help with stress management, work-life balance, practice efficiency, and building resilience. How can I support your wellness journey today?',
      isUser: false,
      timestamp: '10:03 AM'
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(true);
  
  // Create a ref for the messages container
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Conversation starter suggestions
  const suggestions: SuggestionTopic[] = [
    { id: 'stress', text: "Breathing exercises for stress relief", icon: Brain },
    { id: 'balance', text: "Tips for better work-life balance", icon: Heart },
    { id: 'efficiency', text: "How to improve practice efficiency", icon: Zap },
    { id: 'sleep', text: "Techniques for better sleep quality", icon: Clock },
    { id: 'crowdsource', text: "I'd like to share a wellness idea", icon: BookOpen },
  ];
  
  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Handle suggestion click
  const handleSuggestionClick = (suggestion: SuggestionTopic) => {
    setInputValue(suggestion.text);
    setShowSuggestions(false);
    
    // Add a small delay before sending to make it feel more natural
    setTimeout(() => {
      handleSendMessage(suggestion.text);
    }, 500);
  };
  
  // Handle send message
  const handleSendMessage = async (text?: string) => {
    const messageText = text || inputValue;
    if ((!messageText.trim() || isLoading) && !text) return;
    
    // Check if question limit reached
    if (questionCount >= MAX_QUESTIONS) {
      return;
    }
    
    // Add user message
    const newUserMessage: Message = {
      id: Date.now().toString(),
      content: messageText,
      isUser: true,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setInputValue('');
    setIsLoading(true);
    setQuestionCount(prev => prev + 1);
    setShowSuggestions(false);
    
    try {
      // Convert messages to format expected by Anthropic API
      const anthropicMessages = messages.concat(newUserMessage).map(msg => ({
        role: msg.isUser ? 'user' as const : 'assistant' as const,
        content: msg.content
      }));
      
      // Get response from Anthropic API
      const response = await sendMessageToAnthropic(anthropicMessages);
      
      // Add AI response with sources
      const newAiMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: response,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        sources: generateSources(messageText)
      };
      
      setMessages(prev => [...prev, newAiMessage]);
    } catch (error) {
      console.error('Error getting response:', error);
      
      // Add error message
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Generate relevant sources based on the message content
  const generateSources = (message: string): string[] => {
    const sources = [
      "SentientIQ Wellness Library",
      "Methodist Wellness Program (2025)",
      "Journal of Physician Well-being",
      "Mindfulness in Clinical Practice",
      "Work-Life Integration Framework",
      "Clinician Efficiency Handbook",
      "Resilience Building Techniques",
      "Methodist Wellness Community Insights",
      "Digital Wellness Strategies",
      "Sleep Science for Healthcare Professionals"
    ];
    
    // Select 2-3 relevant sources based on the message content
    const relevantSources = [];
    
    if (message.toLowerCase().includes('stress') || message.toLowerCase().includes('breath')) {
      relevantSources.push("Stress Management Protocols (2025)");
      relevantSources.push("Methodist Mindfulness Program");
    }
    
    if (message.toLowerCase().includes('balance') || message.toLowerCase().includes('work-life')) {
      relevantSources.push("Work-Life Integration Framework (Methodist, 2024)");
      relevantSources.push("Journal of Occupational Health Psychology, 2025");
    }
    
    if (message.toLowerCase().includes('efficien') || message.toLowerCase().includes('practice')) {
      relevantSources.push("Clinical Workflow Optimization Guide");
      relevantSources.push("Practice Efficiency Metrics (Methodist, 2025)");
    }
    
    if (message.toLowerCase().includes('sleep')) {
      relevantSources.push("Sleep Quality Research (2024)");
      relevantSources.push("Circadian Rhythm Optimization for Clinicians");
    }
    
    if (message.toLowerCase().includes('share') || message.toLowerCase().includes('idea')) {
      relevantSources.push("Methodist Wellness Community Insights");
      relevantSources.push("Crowdsourced Wellness Strategies Database");
    }
    
    // Add 1-2 random sources if we don't have enough
    while (relevantSources.length < 2) {
      const randomSource = sources[Math.floor(Math.random() * sources.length)];
      if (!relevantSources.includes(randomSource)) {
        relevantSources.push(randomSource);
      }
    }
    
    return relevantSources;
  };
  
  // Colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.500');
  
  return (
    <Box height="100%" display="flex" flexDirection="column" pt="10%">
      {/* Header - more minimalist */}
      <Box p={4} borderBottom="1px" borderColor={borderColor} bg={methodistColors.blue} color="white">
        <HStack spacing={3} justify="space-between">
          <HStack spacing={3}>
            <Avatar 
              size="sm" 
              bg={`${methodistColors.blue}20`}
              icon={<Icon as={MessageCircle} color="white" boxSize={4} />} 
            />
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" fontWeight="medium">JIMI Wellness Coach</Text>
              <Text fontSize="xs">
                <Icon as={Clock} boxSize={3} mr={1} />
                Online
              </Text>
            </VStack>
          </HStack>
          <Badge colorScheme={questionCount >= MAX_QUESTIONS ? "red" : "green"} fontSize="xs">
            {questionCount}/{MAX_QUESTIONS} Questions
          </Badge>
        </HStack>
      </Box>
      
      {/* Chat messages - with scrolling container */}
      <Box flex={1} overflowY="auto" p={4} bg="gray.50">
        <VStack spacing={4} align="stretch">
          {messages.map((message) => (
            <Box 
              key={message.id} 
              bg={message.isUser ? 'gray.50' : `${methodistColors.blue}10`} 
              color={textColor}
              p={4} 
              borderRadius="lg" 
              borderWidth="1px"
              borderColor={message.isUser ? 'gray.100' : `${methodistColors.blue}30`}
              maxW="90%"
              alignSelf={message.isUser ? 'flex-end' : 'flex-start'}
              fontSize="sm"
            >
              <Text>{message.content}</Text>
              
              {/* Sources section */}
              {message.sources && message.sources.length > 0 && (
                <Box mt={2} pt={2} borderTopWidth="1px" borderColor="gray.200">
                  <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                    Sources:
                  </Text>
                  {message.sources.map((source, idx) => (
                    <Text key={idx} fontSize="xs" color={methodistColors.blue}>
                      {source}
                    </Text>
                  ))}
                </Box>
              )}
              
              <Text fontSize="xs" color={secondaryTextColor} textAlign="right" mt={1}>
                {message.timestamp}
              </Text>
            </Box>
          ))}
          
          {/* Show suggestions if no messages from user yet */}
          {showSuggestions && questionCount === 0 && (
            <Box mt={4}>
              <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={2}>
                Try asking about:
              </Text>
              <VStack spacing={2} align="stretch">
                {suggestions.map((suggestion) => (
                  <Button
                    key={suggestion.id}
                    size="sm"
                    variant="outline"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={suggestion.icon} color={methodistColors.blue} />}
                    onClick={() => handleSuggestionClick(suggestion)}
                    fontSize="xs"
                    py={3}
                    px={3}
                    borderRadius="md"
                    borderColor="gray.200"
                    _hover={{ bg: 'gray.50' }}
                  >
                    {suggestion.text}
                  </Button>
                ))}
              </VStack>
            </Box>
          )}
          
          {isLoading && (
            <Flex justify="center" py={4}>
              <Spinner color={methodistColors.blue} size="sm" />
            </Flex>
          )}
          
          {questionCount >= MAX_QUESTIONS && (
            <Alert status="info" borderRadius="md" size="sm">
              <AlertIcon as={AlertCircle} boxSize={4} />
              <Text fontSize="xs">You've reached the maximum number of questions for this session.</Text>
            </Alert>
          )}
          
          <div ref={messagesEndRef} /> {/* Empty div for scrolling */}
        </VStack>
      </Box>
      
      {/* Input area */}
      <Box p={4} borderTop="1px" borderColor={borderColor} bg={cardBg}>
        <InputGroup size="md">
          <Input
            placeholder={questionCount >= MAX_QUESTIONS ? "Question limit reached" : "Type your message..."}
            bg="gray.50"
            borderRadius="full"
            fontSize="sm"
            _focus={{
              borderColor: methodistColors.blue,
              boxShadow: `0 0 0 1px ${methodistColors.blue}`,
            }}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              // Prevent space bar from propagating to parent elements
              if (e.key === ' ') {
                e.stopPropagation();
              }
              if (e.key === 'Enter') {
                handleSendMessage();
              }
            }}
            isDisabled={isLoading || questionCount >= MAX_QUESTIONS}
          />
          <InputRightElement>
            {isLoading ? (
              <Spinner size="sm" color={methodistColors.blue} mr={2} />
            ) : (
              <IconButton
                aria-label="Send message"
                icon={<Icon as={Send} boxSize={4} />}
                size="sm"
                colorScheme="blue"
                variant="ghost"
                onClick={() => handleSendMessage()}
                isDisabled={!inputValue.trim() || questionCount >= MAX_QUESTIONS}
              />
            )}
          </InputRightElement>
        </InputGroup>
      </Box>
    </Box>
  );
};

export default MobileResilienceAssistant;
